import { useAuth } from '../contexts/AuthContext';
import {
  canViewProjects,
  canCreateProjects,
  canUpdateProjects,
  canDeleteProjects,
  canCreateTasks,
  canViewTasks,
  canUpdateTasks,
  canDeleteTasks,
  canAssignTasks,
  canManageUsers,
  canInviteUsers,
  canCreateFiles,
  canViewCalendar,
  canManageCalendar,
} from '../lib/auth/capabilities';
import { getEffectiveSurfaceCapabilities } from '../lib/auth/effectiveSurfaceCapabilities';
import { canResolve } from '../lib/auth/scopedCapabilityResolver';
import { buildScopedRelationshipFacts } from '../lib/auth/scopedRelationshipResolver';

// Simple toast notification function until we have a proper UI component
const toast = (options:  { title: string; description?: string; variant?: string }) => {
  console.log(`[${options.variant?.toUpperCase() || 'INFO'}] ${options.title}${options.description ? `: ${options.description}` : ''}`);
  // In a real app, this would show a UI toast notification
};

export type PermissionAction = 
  | 'projects.view' 
  | 'projects.create' 
  | 'projects.update' 
  | 'projects.delete'
  | 'tasks.create'
  | 'tasks.update'
  | 'tasks.update.assigned'
  | 'tasks.delete'
  | 'tasks.assign'
  | 'users.create'
  | 'files.create'
  | 'comments.create'
  | 'calendar.manage'
  | 'calendar.view';

/**
 * Hook for checking permissions with consistent feedback
 */
export const usePermissions = () => {
  const { user } = useAuth();
   
  const caps = getEffectiveSurfaceCapabilities(user);

  /**
   * Check if user has permission to perform an action
   * @param action The permission action to check
   * @param resourceId Optional resource ID to check against (e.g., project ID)
   * @param options Configuration options
   * @returns Boolean indicating if user has permission
   */
  const checkPermission = (
    _action: PermissionAction,
    resourceId?: string,
    options: {
      showFeedback?: boolean;
      throwError?: boolean;
      feedbackMessage?: string;
    } = { showFeedback: true, throwError: false }
  ): boolean => {
    const { showFeedback, throwError, feedbackMessage } = options;

    // Predicate-first mapping; return boolean or null if not mapped
    const mapToPredicate = (action: PermissionAction): boolean | null => {
      switch (action) {
        case 'projects.view': return canViewProjects(caps);
        case 'projects.create': return canCreateProjects(caps);
        case 'projects.update': return canUpdateProjects(caps);
        case 'projects.delete': return canDeleteProjects(caps);
        case 'tasks.create': return canCreateTasks(caps);
        case 'tasks.update': return canUpdateTasks(caps);
        case 'tasks.delete': return canDeleteTasks(caps);
        case 'tasks.update.assigned': return canUpdateTasks(caps); // Approximation (assigned nuance handled elsewhere soon)
        case 'users.create': return (canManageUsers(caps) || canInviteUsers(caps));
        case 'files.create': return canCreateFiles(caps);
        case 'tasks.assign': return canAssignTasks(caps);
        case 'comments.create': return canViewTasks(caps);
        case 'calendar.manage': return canManageCalendar(caps);
        case 'calendar.view': return canViewCalendar(caps);
        default:
          return null;
      }
    };

    const predicateResult = mapToPredicate(_action);
    const hasAccess = predicateResult ?? false;
    if (predicateResult === null) {
      if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production') {
        console.warn(`[Capabilities] Unmapped action '${_action}' denied by default.`);
      }
    }
    
    // Handle case when permission is denied
    if (!hasAccess) {
      const defaultMessage = `You don't have permission to ${_action.split('.')[1]} ${_action.split('.')[0]}`;
      const message = feedbackMessage || defaultMessage;
      
      if (showFeedback) {
        toast({
          title: "Permission Denied",
          description: message,
          variant: "destructive"
        });
      }
      
      if (throwError) {
        throw new Error(message);
      }
    }
    
    return hasAccess;
  };

  /**
   * Check if the user can access a specific project.
   *
   * Resolves `project.view` for the exact project through the single canonical
   * engine (account role + scoped-relationship facts + lifecycle). Legacy role
   * strings and mutable project metadata (managerId / teamMembers) are NEVER
   * used as authority. Server RLS remains the final gate.
   */
  const canAccessProject = (_projectData:  {
    id: string;
    clientId: string;
    managerId: string;
    teamMembers: string[];
  }): boolean => {
    const allowed = canResolve(
      {
        userId: user?.id,
        accountRole: user?.account_role,
        platformRole: user?.platform_role,
        workspaceRole: user?.workspace_role,
        scopedRelationshipFacts: user ? buildScopedRelationshipFacts(user) : undefined,
        explicitRights: [],
        suspended: user?.status === 'suspended',
      },
      'project.view',
      { projectId: _projectData.id },
    );

    if (!allowed && user) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to view this project",
        variant: "destructive"
      });
    }

    return allowed;
  };

  return {
    checkPermission,
    canAccessProject
  };
};
